import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useAssignProductFamily, useUnassignProductFamily } from '../../hooks';
import { resolveLocalizedText } from '../../lib/localizedText';
import type { ToastSeverity } from '../../lib/useToast';
import type {
  AttributeCompleteness,
  AttributeDefinition,
  AttributeValue,
  ProductFamily,
} from '../../model/types';

export interface ProductFamilyPanelProps {
  productId: string;
  families: ProductFamily[];
  familiesLoading: boolean;
  assignedFamily: ProductFamily | null;
  /** True when a family is assigned but its `code` couldn't be resolved (see `useResolvedProductFamily`). */
  unresolvable: boolean;
  attributeValues: AttributeValue[];
  attributeCompleteness?: AttributeCompleteness | null;
  /** The attribute dictionary, for axis-chip / missing-attribute-chip labels. */
  dictionary: AttributeDefinition[];
  canManage: boolean;
  onNotify: (severity: ToastSeverity, message: string) => void;
  /** Tells the parent's `useResolvedProductFamily` which `code` this session just assigned (or `null` on unassign). */
  onFamilyCodeAssigned: (code: string | null) => void;
}

type ConfirmAction = { kind: 'assign'; family: ProductFamily } | { kind: 'unassign' };

/**
 * Family selector + variant-axis chips + attribute-completeness widget for `ProductEditPage`
 * (user-story-merchant-portal-030 slice 1). A controlled component — family resolution
 * (`assignedFamily`/`unresolvable`) is computed once by `useResolvedProductFamily` and shared with
 * the page (which also needs it to drive the typed-attributes panel and the variants section), not
 * duplicated here. Assign/unassign mutations update the shared `ProductDetail` query cache, so
 * `attributeValues`/`attributeCompleteness` refresh via normal prop flow — no callback threading
 * needed beyond reporting the just-assigned `code` back up (session-local resolution memory only,
 * not itself part of the server cache).
 */
export function ProductFamilyPanel({
  productId,
  families,
  familiesLoading,
  assignedFamily,
  unresolvable,
  attributeValues,
  attributeCompleteness,
  dictionary,
  canManage,
  onNotify,
  onFamilyCodeAssigned,
}: ProductFamilyPanelProps) {
  const { t, i18n } = useTranslation('catalog');
  const assign = useAssignProductFamily();
  const unassign = useUnassignProductFamily();

  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const attributeLabel = (code: string): string => {
    const def = dictionary.find((a) => a.code === code);
    return def ? resolveLocalizedText(def.translations, 'label', i18n.language, def.code) : code;
  };
  const familyLabel = (family: ProductFamily): string =>
    `${resolveLocalizedText(family.translations, 'name', i18n.language, family.code)} (${family.code})`;

  const hasValues = attributeValues.length > 0;
  const pending = assign.isPending || unassign.isPending;

  const doAssign = (family: ProductFamily) => {
    assign.mutate(
      { productId, body: { familyCode: family.code } },
      {
        onSuccess: () => {
          onFamilyCodeAssigned(family.code);
          onNotify('success', t('productFamilyPanel.assigned', { name: familyLabel(family) }));
        },
        onError: (error) => onNotify('error', error.message),
      },
    );
  };

  const doUnassign = () => {
    unassign.mutate(
      { productId },
      {
        onSuccess: () => {
          onFamilyCodeAssigned(null);
          onNotify('success', t('productFamilyPanel.unassigned'));
        },
        onError: (error) => onNotify('error', error.message),
      },
    );
  };

  const handleAutocompleteChange = (value: ProductFamily | null) => {
    if (value === null) {
      if (hasValues) setConfirmAction({ kind: 'unassign' });
      else doUnassign();
      return;
    }
    if (assignedFamily && value.code === assignedFamily.code) return;
    if (hasValues) setConfirmAction({ kind: 'assign', family: value });
    else doAssign(value);
  };

  const confirmDialogAction = () => {
    if (!confirmAction) return;
    if (confirmAction.kind === 'unassign') doUnassign();
    else doAssign(confirmAction.family);
    setConfirmAction(null);
  };

  const axisAttributes = assignedFamily?.attributes.filter((a) => a.variantAxis) ?? [];
  const completenessPct =
    attributeCompleteness && attributeCompleteness.required > 0
      ? Math.round((attributeCompleteness.filled / attributeCompleteness.required) * 100)
      : null;

  return (
    <Stack spacing={2}>
      <Autocomplete
        options={families}
        value={assignedFamily}
        loading={familiesLoading}
        onChange={(_e, value) => handleAutocompleteChange(value)}
        getOptionLabel={(option) => familyLabel(option)}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        disabled={!canManage || pending}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('productFamilyPanel.selectorLabel')}
            helperText={unresolvable ? t('productFamilyPanel.unresolvedHint') : t('productFamilyPanel.selectorHelper')}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {pending || familiesLoading ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />

      {assignedFamily ? (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {t('productFamilyPanel.axisLabel')}
          </Typography>
          {axisAttributes.length > 0 ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {axisAttributes.map((a) => (
                <Chip
                  key={a.attributeCode}
                  size="small"
                  variant="outlined"
                  color="primary"
                  label={attributeLabel(a.attributeCode)}
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('productFamilies.noAxis')}
            </Typography>
          )}
        </Box>
      ) : null}

      {attributeCompleteness ? (
        <>
          <Divider />
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
              <Typography variant="caption" color="text.secondary">
                {t('productFamilyPanel.completeness.label')}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {t('productFamilyPanel.completeness.summary', {
                  filled: attributeCompleteness.filled,
                  required: attributeCompleteness.required,
                })}
              </Typography>
            </Stack>
            {completenessPct !== null ? (
              <LinearProgress
                variant="determinate"
                value={completenessPct}
                color={completenessPct >= 100 ? 'success' : 'warning'}
                sx={{ borderRadius: 1, height: 6, mb: attributeCompleteness.missing.length > 0 ? 1 : 0 }}
              />
            ) : null}
            {attributeCompleteness.missing.length > 0 ? (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {attributeCompleteness.missing.map((code) => (
                  <Chip key={code} size="small" color="warning" variant="outlined" label={attributeLabel(code)} />
                ))}
              </Stack>
            ) : null}
          </Box>
        </>
      ) : null}

      <Dialog open={confirmAction !== null} onClose={() => setConfirmAction(null)}>
        <DialogTitle>
          {confirmAction?.kind === 'unassign'
            ? t('productFamilyPanel.confirmUnassign.title')
            : t('productFamilyPanel.confirmReassign.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction?.kind === 'unassign'
              ? t('productFamilyPanel.confirmUnassign.body')
              : t('productFamilyPanel.confirmReassign.body')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>{t('common:action.cancel')}</Button>
          <Button color="warning" variant="contained" onClick={confirmDialogAction}>
            {t('common:action.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
